import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import { isMobile } from 'react-device-detect';

const useStyles = makeStyles((theme) => ({
  ShortcutpageContainer: {
    fontFamily: "'Inter', sans-serif",
    textAlign: 'center',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    gap: '20px',
    '@media (orientation: landscape) and (max-height: 600px)': {
      padding: '10px',
      gap: '10px',
    },
  },
  headerText: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: '20px',
    textShadow: '0px 0px 10px rgba(77, 121, 255, 0.8)',
  },

  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    margin: '0 auto',
    width: '100%',
    maxWidth: '1200px',
    flex: 1,
  },
  columnsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    alignItems: 'flex-start',
    flex: 1,
    '@media (max-width: 900px)': {
      flexDirection: 'column',
    },
  },
  column: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  columnTitle: {
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: '#9ea4d8',
    marginBottom: 4,
  },
  ShortcutpageButton: {
    backgroundColor: '#1e1e1e',
    color: '#ffffff',
    padding: '10px 15px',
    fontSize: '1.05rem',
    borderRadius: '25px',
    border: '1px solid #4d79ff',
    fontWeight: '700',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    cursor: 'pointer',
    position: 'relative',
    textTransform: 'none',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease',
    height: '64px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
      backgroundColor: '#292929',
    },
    
    // Adjust button height for different screen sizes
    '@media (orientation: landscape) and (max-height: 600px)': {
      height: '46px',
      fontSize: '0.95rem',
      padding: '5px 10px',
    },
    
    '@media (max-width: 768px)': {
      height: '60px',
      fontSize: '0.95rem',
    },
    
    '@media (max-width: 480px)': {
      height: '54px',
      fontSize: '0.9rem',
      padding: '8px 12px',
    },
  },  

  newBadge: {
    position: 'absolute',
    top: '-10px',
    right: '-10px',
    backgroundColor: '#ff6f00',
    color: '#fff',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
  },
}));

const ShortcutPage = ({ setShowHomePage, setShowShortcutPage, setShowE3DMarketplace, setShowCustomNFTMarketplace, setShowSwapDialog, setShowStakeDialog }) => {
  console.log('ShortcutPage props check:', { 
    hasSetShowSwapDialog: typeof setShowSwapDialog === 'function', 
    hasSetShowStakeDialog: typeof setShowStakeDialog === 'function'
  });
  
  const classes = useStyles();

  const navigate = useNavigate();
  const location = useLocation();

  const goTo = (path, specialAction) => {
    // Check for special paths that should open as dialogs
    if (specialAction) {
      console.log('ShortcutPage: Executing special action instead of navigating');
      // Execute the specialAction directly - don't rely on closure
      if (path === '/swap' && typeof setShowSwapDialog === 'function') {
        console.log('ShortcutPage: Directly calling setShowSwapDialog(true)');
        // Use the App component's debugSetShowSwapDialog via props
        setShowSwapDialog(true);
        // Important: prevent navigation to the swap page - stay on current page
        return;
      } else if (path === '/stake' && typeof setShowStakeDialog === 'function') {
        console.log('ShortcutPage: Directly calling setShowStakeDialog(true)');
        // Use the App component's debugSetShowStakeDialog via props
        setShowStakeDialog(true);
        // Important: prevent navigation to the stake page - stay on current page
        return;
      } else {
        // Fall back to the provided specialAction
        specialAction();
        return;
      }
    }
    if (typeof setShowShortcutPage === 'function') {
      setShowShortcutPage(false);
    }
    navigate(path, {
      state: { from: location.pathname }
    });
  };

  // Special direct handlers for dialog buttons that use global window functions
  const handleOpenSwapDialog = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('ShortcutPage: Using global function to open Swap dialog');
    
    // Use the global function instead of props
    if (typeof window.openE3DSwapDialog === 'function') {
      window.openE3DSwapDialog();
    } else {
      console.error('ShortcutPage: Global openE3DSwapDialog function not found!');
    }
    
    return false;
  };

  const handleOpenStakeDialog = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('ShortcutPage: Using global function to open Stake dialog');
    
    // Use the global function instead of props
    if (typeof window.openE3DStakeDialog === 'function') {
      window.openE3DStakeDialog();
    } else {
      console.error('ShortcutPage: Global openE3DStakeDialog function not found!');
    }
    
    return false;
  };

  // Column definitions
  const exploreButtons = [
    { label: 'Token galaxy', path: '/token', tooltip: 'Spinning 3D map of active tokens by recent activity' },
    { label: 'Graphs', path: '/graphs', tooltip: 'Graph of wallets, tokens, and transaction evidence' },
    { label: 'NFT graph', path: '/token?nft=true', tooltip: 'Graph view of NFTs and their relationships' },
    { label: 'NFT explorer', path: '/nft', tooltip: 'Search and explore NFT collections in detail' },
    { label: 'On-chain stories', path: '/stories', tooltip: 'AI-generated on-chain stories backed by E3D views', isNew: true },
  ];

  const tradeButtons = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      tooltip: 'Overview of markets, new tokens, NFTs, and on-chain stories',
    },
    {
      label: 'Token prices',
      path: '/price',
      tooltip: 'View current token prices and recent price changes',
    },
    {
      label: 'Price bubbles',
      path: '/bubbles',
      tooltip: 'Bubble visualization of the strongest price moves',
      isNew: true,
    },
    { 
      label: 'Swap', 
      path: '/swap', 
      tooltip: 'Swap ETH for tokens directly in E3D', 
      directHandler: handleOpenSwapDialog
    },
    { 
      label: 'Stake', 
      path: '/stake', 
      tooltip: 'Stake your tokens for rewards', 
      directHandler: handleOpenStakeDialog
    },
    { label: 'E3D NFT Marketplace', path: '/marketplace', tooltip: 'Buy and sell NFTs in the E3D NFT Marketplace' },
  ];

  const discoverButtons = [
    { label: 'New token launches', path: '/token?new=true', tooltip: 'Discover newly added tokens' },
    { label: 'New NFT drops', path: '/token?new=true&nft=true', tooltip: 'Browse recently added NFTs' },
    { label: "What’s new in E3D", path: '/whatsnew', tooltip: 'See what\'s new in E3D' },
    { label: 'Market news', path: '/news', tooltip: 'AI-generated newsletter on blockchain activity' },
    ...(!isMobile ? [{ label: 'PCAP analyzer', path: '/pcap', tooltip: 'Open and analyze packet capture (PCAP) files' }] : []),
    { label: 'Help', path: '/docs?doc=e3d_user_guide.html', tooltip: 'Read the documentation and user guide' },
  ];

  return (
    <div className={classes.ShortcutpageContainer}>
      <div className={classes.buttonsContainer}>
        <div className={classes.columnsRow}>
          <div className={classes.column}>
            <div className={classes.columnTitle}>Trade &amp; earn</div>
            {tradeButtons.map(({ label, path, isNew, tooltip, specialAction, directHandler }) => (
              <Tooltip key={label} title={tooltip} arrow placement="top">
                <button
                  className={classes.ShortcutpageButton}
                  onClick={directHandler || (() => goTo(path, specialAction))}
                >
                  {label}
                  {isNew && <div className={classes.newBadge}>NEW</div>}
                </button>
              </Tooltip>
            ))}
          </div>

          <div className={classes.column}>
            <div className={classes.columnTitle}>Explore</div>
            {exploreButtons.map(({ label, path, isNew, tooltip, specialAction, directHandler }) => (
              <Tooltip key={label} title={tooltip} arrow placement="top">
                <button
                  className={classes.ShortcutpageButton}
                  onClick={directHandler || (() => goTo(path, specialAction))}
                >
                  {label}
                  {isNew && <div className={classes.newBadge}>NEW</div>}
                </button>
              </Tooltip>
            ))}
          </div>

          <div className={classes.column}>
            <div className={classes.columnTitle}>Discover &amp; tools</div>
            {discoverButtons.map(({ label, path, isNew, tooltip, specialAction, directHandler }) => (
              <Tooltip key={label} title={tooltip} arrow placement="top">
                <button
                  className={classes.ShortcutpageButton}
                  onClick={directHandler || (() => goTo(path, specialAction))}
                >
                  {label}
                  {isNew && <div className={classes.newBadge}>NEW</div>}
                </button>
              </Tooltip>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortcutPage;
